import React, { useState, useMemo } from 'react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, ComposedChart
} from 'recharts';
import TransactionMap from './TransactionMap';
import './StatsDashboard.css';

// Apple-inspired colors
const COLORS = ['#007aff', '#34c759', '#ff9500', '#ff3b30', '#af52de', '#5856d6', '#00c7be', '#ff2d55'];

function StatsDashboard({ transactions, summary }) {
  const [timeRange, setTimeRange] = useState('12'); // months

  // Parse amount helper
  const parseAmount = (amountStr) => {
    if (typeof amountStr === 'number') return amountStr;
    if (!amountStr) return 0;
    const cleaned = amountStr.replace(/[$,]/g, '');
    return parseFloat(cleaned) || 0;
  };

  // Categories that represent internal money movements (not real spending/income)
  const isInternalTransfer = (t) => {
    const category = (t.SmartCategory || t.Category || '').toLowerCase();
    return category.includes('credit card payment') ||
           category.includes('internal transfer') ||
           category.includes('savings/transfer') ||
           category.includes('savings transfer') ||
           category === 'transfer' ||
           category === 'savings';
  };

  // Exclude expenses: IsExcluded flag OR internal transfers (except wire transfers)
  const shouldExcludeExpense = (t) => {
    // Wire transfers should always be counted as expenses (buying land, etc.)
    if (t.Description?.toLowerCase().includes('wire transfer')) {
      return false;
    }
    // Internal transfers should not be counted as spending
    if (isInternalTransfer(t)) {
      return true;
    }
    return t.IsExcluded === true;
  };

  // Exclude income: Internal transfers (CC payments received, savings transfers received)
  const shouldExcludeIncome = (t) => {
    // Internal transfers appearing as income on receiving accounts
    if (isInternalTransfer(t)) {
      return true;
    }
    return t.IsExcluded === true;
  };

  // Filter transactions by time range
  const filteredTransactions = useMemo(() => {
    const months = parseInt(timeRange);
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - months);

    return transactions.filter(t => {
      if (!t.Date) return false;
      const parts = t.Date.split('/');
      if (parts.length < 3) return false;
      const txDate = new Date(parts[2], parts[0] - 1, parts[1]);
      return txDate >= cutoff;
    });
  }, [transactions, timeRange]);

  // Monthly income vs expenses data
  const monthlyData = useMemo(() => {
    const byMonth = {};

    filteredTransactions.forEach(t => {
      if (!t.Date) return;
      const parts = t.Date.split('/');
      if (parts.length < 3) return;
      const monthKey = `${parts[2]}-${parts[0].padStart(2, '0')}`;
      const amount = parseAmount(t.Amount);

      if (!byMonth[monthKey]) {
        byMonth[monthKey] = { month: monthKey, income: 0, expenses: 0, net: 0 };
      }

      // Count income (excluding CC payments), expenses (excluding marked excluded)
      if (amount > 0) {
        if (!shouldExcludeIncome(t)) {
          byMonth[monthKey].income += amount;
        }
      } else if (!shouldExcludeExpense(t)) {
        byMonth[monthKey].expenses += Math.abs(amount);
      }
    });

    return Object.values(byMonth)
      .map(m => ({ ...m, net: m.income - m.expenses }))
      .sort((a, b) => a.month.localeCompare(b.month));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTransactions]);

  // Category breakdown for pie chart
  const categoryData = useMemo(() => {
    const byCategory = {};

    filteredTransactions.forEach(t => {
      const amount = parseAmount(t.Amount);
      if (amount >= 0) return; // Only expenses
      if (shouldExcludeExpense(t)) return; // Skip excluded categories

      const category = t.SmartCategory || t.Category || 'Uncategorized';
      if (!byCategory[category]) {
        byCategory[category] = 0;
      }
      byCategory[category] += Math.abs(amount);
    });

    return Object.entries(byCategory)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTransactions]);

  // Account breakdown
  const accountData = useMemo(() => {
    const byAccount = {};

    filteredTransactions.forEach(t => {
      const account = t.Account || 'Unknown';
      const amount = parseAmount(t.Amount);

      if (!byAccount[account]) {
        byAccount[account] = { name: account, income: 0, expenses: 0, transactions: 0 };
      }

      byAccount[account].transactions++;
      if (amount > 0) {
        byAccount[account].income += amount;
      } else {
        byAccount[account].expenses += Math.abs(amount);
      }
    });

    return Object.values(byAccount).sort((a, b) => b.transactions - a.transactions);
  }, [filteredTransactions]);

  // Daily spending trend
  const dailySpending = useMemo(() => {
    const byDay = {};
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    filteredTransactions.forEach(t => {
      if (!t.Date) return;
      const parts = t.Date.split('/');
      if (parts.length < 3) return;
      const txDate = new Date(parts[2], parts[0] - 1, parts[1]);
      if (txDate < thirtyDaysAgo) return;

      const dayKey = `${parts[0]}/${parts[1]}`;
      const amount = parseAmount(t.Amount);

      if (!byDay[dayKey]) {
        byDay[dayKey] = { day: dayKey, spending: 0, count: 0 };
      }

      if (amount < 0) {
        byDay[dayKey].spending += Math.abs(amount);
        byDay[dayKey].count++;
      }
    });

    return Object.values(byDay).slice(-30);
  }, [filteredTransactions]);

  // Top merchants (excluding payments/transfers)
  const topMerchants = useMemo(() => {
    const byMerchant = {};

    filteredTransactions.forEach(t => {
      const amount = parseAmount(t.Amount);
      if (amount >= 0) return;
      if (shouldExcludeExpense(t)) return; // Skip credit card payments, transfers, etc.

      let merchant = t.Description || 'Unknown';
      // Clean up merchant name
      merchant = merchant.substring(0, 30).trim();

      if (!byMerchant[merchant]) {
        byMerchant[merchant] = { name: merchant, total: 0, count: 0 };
      }
      byMerchant[merchant].total += Math.abs(amount);
      byMerchant[merchant].count++;
    });

    return Object.values(byMerchant)
      .filter(m => m.count >= 2)
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTransactions]);

  // Calculate KPIs (count all income, exclude only expenses marked as excluded)
  const kpis = useMemo(() => {
    let totalIncome = 0;
    let totalExpenses = 0;
    let expenseCount = 0;

    filteredTransactions.forEach(t => {
      const amount = parseAmount(t.Amount);
      if (amount > 0) {
        if (!shouldExcludeIncome(t)) {
          totalIncome += amount;
        }
      } else if (!shouldExcludeExpense(t)) {
        totalExpenses += Math.abs(amount);
        expenseCount++;
      }
    });

    const months = parseInt(timeRange);
    const avgMonthlyIncome = totalIncome / months;
    const avgMonthlyExpenses = totalExpenses / months;
    const savingsRate = totalIncome > 0 ? ((totalIncome - totalExpenses) / totalIncome * 100) : 0;

    return {
      totalIncome,
      totalExpenses,
      netCashFlow: totalIncome - totalExpenses,
      avgMonthlyIncome,
      avgMonthlyExpenses,
      savingsRate,
      transactionCount: filteredTransactions.length,
      avgTransactionSize: expenseCount > 0 ? totalExpenses / expenseCount : 0
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTransactions, timeRange]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatK = (value) => {
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}k`;
    return `$${value.toFixed(0)}`;
  };

  return (
    <div className="stats-dashboard">
      {/* Time Range Selector */}
      <div className="stats-header">
        <h2>Statistics Dashboard</h2>
        <div className="time-selector">
          <button className={timeRange === '3' ? 'active' : ''} onClick={() => setTimeRange('3')}>3M</button>
          <button className={timeRange === '6' ? 'active' : ''} onClick={() => setTimeRange('6')}>6M</button>
          <button className={timeRange === '12' ? 'active' : ''} onClick={() => setTimeRange('12')}>1Y</button>
          <button className={timeRange === '24' ? 'active' : ''} onClick={() => setTimeRange('24')}>2Y</button>
          <button className={timeRange === '60' ? 'active' : ''} onClick={() => setTimeRange('60')}>5Y</button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="kpi-grid">
        <div className="kpi-card income">
          <div className="kpi-label">Total Income</div>
          <div className="kpi-value">{formatCurrency(kpis.totalIncome)}</div>
          <div className="kpi-sub">{formatCurrency(kpis.avgMonthlyIncome)}/mo avg</div>
        </div>
        <div className="kpi-card expenses">
          <div className="kpi-label">Total Expenses</div>
          <div className="kpi-value">{formatCurrency(kpis.totalExpenses)}</div>
          <div className="kpi-sub">{formatCurrency(kpis.avgMonthlyExpenses)}/mo avg</div>
        </div>
        <div className="kpi-card net">
          <div className="kpi-label">Net Cash Flow</div>
          <div className={`kpi-value ${kpis.netCashFlow >= 0 ? 'positive' : 'negative'}`}>
            {formatCurrency(kpis.netCashFlow)}
          </div>
          <div className="kpi-sub">{kpis.savingsRate.toFixed(1)}% savings rate</div>
        </div>
        <div className="kpi-card transactions">
          <div className="kpi-label">Transactions</div>
          <div className="kpi-value">{kpis.transactionCount.toLocaleString()}</div>
          <div className="kpi-sub">{formatCurrency(kpis.avgTransactionSize)} avg</div>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="charts-row">
        {/* Monthly Income vs Expenses */}
        <div className="chart-container large">
          <h3>Monthly Income vs Expenses</h3>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e8e8ed" />
              <XAxis dataKey="month" stroke="#86868b" tick={{ fontSize: 11 }} />
              <YAxis stroke="#86868b" tickFormatter={formatK} />
              <Tooltip
                formatter={(value) => formatCurrency(value)}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e8e8ed', borderRadius: '8px' }}
              />
              <Legend />
              <Bar dataKey="income" fill="#34c759" name="Income" />
              <Bar dataKey="expenses" fill="#ff3b30" name="Expenses" />
              <Line type="monotone" dataKey="net" stroke="#007aff" strokeWidth={2} name="Net" dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Category Pie Chart */}
        <div className="chart-container">
          <h3>Spending by Category</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={categoryData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={85}
                paddingAngle={2}
                dataKey="value"
                label={({ name, percent }) => `${name.substring(0, 10)} ${(percent * 100).toFixed(0)}%`}
                labelLine={true}
              >
                {categoryData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => formatCurrency(value)} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="charts-row">
        {/* Net Cash Flow Trend */}
        <div className="chart-container">
          <h3>Net Cash Flow Trend</h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e8e8ed" />
              <XAxis dataKey="month" stroke="#86868b" tick={{ fontSize: 10 }} />
              <YAxis stroke="#86868b" tickFormatter={formatK} />
              <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ backgroundColor: '#fff', border: '1px solid #e8e8ed', borderRadius: '8px' }} />
              <Area
                type="monotone"
                dataKey="net"
                stroke="#007aff"
                fill="url(#colorNet)"
                strokeWidth={2}
              />
              <defs>
                <linearGradient id="colorNet" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#007aff" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#007aff" stopOpacity={0.05} />
                </linearGradient>
              </defs>
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Account Activity */}
        <div className="chart-container">
          <h3>Activity by Account</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={accountData.slice(0, 6)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e8e8ed" />
              <XAxis type="number" tickFormatter={formatK} stroke="#86868b" />
              <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 10 }} stroke="#86868b" />
              <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ backgroundColor: '#fff', border: '1px solid #e8e8ed', borderRadius: '8px' }} />
              <Legend />
              <Bar dataKey="income" fill="#34c759" name="Income" stackId="a" />
              <Bar dataKey="expenses" fill="#ff3b30" name="Expenses" stackId="a" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Merchants Table */}
      <div className="stats-section">
        <h3>Top Spending Merchants</h3>
        <div className="merchants-table">
          <div className="table-header">
            <span className="col-rank">#</span>
            <span className="col-name">Merchant</span>
            <span className="col-count">Count</span>
            <span className="col-total">Total</span>
            <span className="col-avg">Avg</span>
          </div>
          {topMerchants.map((m, idx) => (
            <div key={idx} className="table-row">
              <span className="col-rank">{idx + 1}</span>
              <span className="col-name">{m.name}</span>
              <span className="col-count">{m.count}</span>
              <span className="col-total">{formatCurrency(m.total)}</span>
              <span className="col-avg">{formatCurrency(m.total / m.count)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Daily Spending Heatmap-style */}
      <div className="stats-section">
        <h3>Last 30 Days Spending</h3>
        <ResponsiveContainer width="100%" height={150}>
          <BarChart data={dailySpending}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e8e8ed" />
            <XAxis dataKey="day" stroke="#86868b" tick={{ fontSize: 9 }} />
            <YAxis stroke="#86868b" tickFormatter={formatK} />
            <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ backgroundColor: '#fff', border: '1px solid #e8e8ed', borderRadius: '8px' }} />
            <Bar dataKey="spending" fill="#ff3b30" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Transaction Map */}
      <TransactionMap transactions={filteredTransactions} />
    </div>
  );
}

export default StatsDashboard;
